import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Mail, Phone, Building2, Trash2, MessageSquare } from "lucide-react";

const STATUS = [
  { v: "new", label: "Nouveau", color: "text-[#E60012]" },
  { v: "contacted", label: "Contacté", color: "text-yellow-400" },
  { v: "done", label: "Conclu", color: "text-emerald-400" },
];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-CA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

const InquiriesPanel = () => {
  const [items, setItems] = useState([]);

  const refresh = () => {
    api.get("/admin/sponsor-inquiries").then((r) => setItems(r.data)).catch(() => setItems([]));
  };

  useEffect(() => {
    refresh();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/sponsor-inquiries/${id}?status=${status}`);
      refresh();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cette demande ?")) return;
    try {
      await api.delete(`/admin/sponsor-inquiries/${id}`);
      refresh();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const newCount = items.filter((i) => i.status === "new").length;

  return (
    <div data-testid="admin-inquiries-panel">
      <div className="mb-6">
        <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">Boîte de réception</span>
        <h2 className="mt-1 font-display text-4xl uppercase">
          Demandes commanditaires {newCount > 0 && <span className="text-[#E60012] text-2xl">({newCount} nouv.)</span>}
        </h2>
      </div>

      {items.length === 0 ? (
        <div className="bg-[#111] border border-white/10 p-12 text-center">
          <Mail className="w-10 h-10 text-[#E60012] mx-auto mb-3" />
          <div className="font-display text-2xl uppercase">Aucune demande pour le moment</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((q) => {
            const st = STATUS.find((s) => s.v === q.status) || STATUS[0];
            return (
              <div key={q.id} className="bg-[#111] border border-white/10 p-5" data-testid={`inquiry-${q.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <Building2 className="w-4 h-4 text-[#E60012]" />
                      <h3 className="font-display text-2xl uppercase">{q.company_name}</h3>
                      <span className={`text-xs font-condensed uppercase tracking-wider ${st.color}`}>● {st.label}</span>
                    </div>
                    <div className="text-sm text-white/65">{q.contact_name}</div>
                    <div className="mt-2 flex items-center gap-4 flex-wrap text-sm text-white/70">
                      <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 hover:text-[#E60012]">
                        <Mail className="w-3.5 h-3.5" /> {q.email}
                      </a>
                      {q.phone && (
                        <a href={`tel:${q.phone}`} className="flex items-center gap-1.5 hover:text-[#E60012]">
                          <Phone className="w-3.5 h-3.5" /> {q.phone}
                        </a>
                      )}
                      {q.desired_tier && (
                        <span className="px-2 py-0.5 border border-white/15 text-xs font-condensed uppercase tracking-wider">
                          Niveau : {q.desired_tier}
                        </span>
                      )}
                    </div>
                    {q.message && (
                      <div className="mt-3 bg-black/40 border-l-2 border-[#E60012] p-3 text-sm text-white/80 flex gap-2">
                        <MessageSquare className="w-3.5 h-3.5 mt-0.5 text-[#E60012] shrink-0" />
                        <span className="whitespace-pre-line">{q.message}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="text-xs text-white/45 font-condensed uppercase tracking-wider">{fmtDate(q.created_at)}</span>
                    <div className="flex gap-1">
                      {STATUS.map((s) => (
                        <button
                          key={s.v}
                          onClick={() => setStatus(q.id, s.v)}
                          data-testid={`status-${q.id}-${s.v}`}
                          className={`px-2 py-1 text-[10px] font-condensed uppercase tracking-wider border transition-colors ${
                            q.status === s.v
                              ? "bg-[#E60012] text-white border-[#E60012]"
                              : "border-white/15 text-white/70 hover:border-[#E60012]"
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                      <button
                        onClick={() => remove(q.id)}
                        data-testid={`del-inquiry-${q.id}`}
                        className="p-1.5 border border-white/15 hover:border-red-500 hover:text-red-400"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InquiriesPanel;
